import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DndContext, DragOverlay, closestCenter, } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import { observer } from 'mobx-react';
import { useCallback, useState } from 'react';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { useAction, useSlideUIContext } from '@marvelapp/ballpark-application';
import { Stack } from '@marvelapp/ballpark-components';
import { CardSortingUtils, StepUtils } from '@marvelapp/user-test-creator';
import { useDragging } from '../../../hooks/useDragging';
import SlideContent from '../../common/SlideContent';
import { BaseAddNewCategory as AddNewCategory } from '../BaseAddNewCategory';
import { CardsProgressHeading } from '../CardsProgressHeading';
import { FocusManagedList } from '../FocusManagedList';
import { dropAnimation } from '../dndUtils';
import { AddNewCard } from './AddNewCard';
import { BulkAddItemsModal } from './BulkAddItemsModal';
import { Card } from './Card';
import { CardSortingCategoryGrid } from './CardSortingCategoryGrid';
import { CardSortingSidebar } from './CardSortingSidebar';
import { Category } from './Category';
export const CardSortingBuilder = observer(function CardSortingBuilder({ onMainCTAClick, }) {
    const { step } = useSlideUIContext();
    const [activeId, setActiveId] = useState(null);
    const { handleDragEnd, sensors } = useDragging();
    if (!StepUtils.isCardSortingStep(step)) {
        throw new Error('CardSortingBuilder can only be used with a CardSortingStep');
    }
    const canAddNewCategories = CardSortingUtils.canBuilderAddNewCategories(step);
    const onCardDragStart = useCallback(({ active }) => {
        if (!active)
            return;
        setActiveId(active.id);
    }, []);
    const onCardDragEnd = useAction((event) => {
        setActiveId(null);
        if (!StepUtils.isCardSortingStep(step))
            return null;
        handleDragEnd({
            event,
            items: step.cards,
            reorderFn: CardSortingUtils.reorderCards,
            parentItem: step,
        });
    }, [handleDragEnd, step]);
    const onCategoryDragStart = onCardDragStart;
    const onCategoryDragEnd = useAction((event) => {
        if (!StepUtils.isCardSortingStep(step))
            return null;
        handleDragEnd({
            event,
            items: step.categories,
            reorderFn: CardSortingUtils.reorderCategories,
            parentItem: step,
        });
    }, [handleDragEnd, step]);
    const onAddNewCard = useAction(() => {
        CardSortingUtils.addCard(step);
    }, [step]);
    const onAddNewCategory = useAction(() => {
        CardSortingUtils.addCategory(step);
    }, [step]);
    const cards = CardSortingUtils.getCards(step);
    const categories = CardSortingUtils.getCategories(step);
    const activeCard = cards.find((card) => card.id === activeId);
    const activeCategory = categories.find((category) => category.id === activeId);
    const [isBulkAddCardsModalOpen, setBulkAddCardsModalOpen] = useState(false);
    const handleBulkAddCardsClick = useCallback(() => {
        setBulkAddCardsModalOpen(true);
    }, []);
    const [isBulkAddCategoriesModalOpen, setBulkAddCategoriesModalOpen] = useState(false);
    const handleBulkAddCategoriesClick = useCallback(() => {
        setBulkAddCategoriesModalOpen(true);
    }, []);
    return (_jsxs(Stack, { className: "rounded-b-lg bg-white", direction: "row", height: "full", width: "full", children: [_jsx(CardSortingSidebar, { "data-testid": "card-sort-cards", children: _jsx("div", { className: "px-3 pt-3", children: _jsx(SlideContent, { ctaOnClick: onMainCTAClick, children: _jsxs("div", { className: "-ml-3 mt-6 w-[calc(100%+24px)] rounded-lg bg-gray-100 p-4", children: [_jsx("div", { className: "mb-6", children: _jsx(CardsProgressHeading, { nonInteractive: true, total: cards.length, unsorted: cards.length }) }), _jsxs(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragStart: onCardDragStart, onDragEnd: onCardDragEnd, children: [_jsx(Stack, { align: "center", gap: "4", children: _jsx(SortableContext, { items: cards, strategy: rectSortingStrategy, children: _jsx(FocusManagedList, { items: cards, onAddItem: onAddNewCard, renderItem: ({ key, item, ref, onKeyDown }) => (_jsx(Card, { card: item, titleInputRef: ref, onTitleKeyDown: onKeyDown }, key)), renderAddItem: ({ addItem }) => (_jsx(AddNewCard, { onClick: addItem, onBulkAddClick: handleBulkAddCardsClick })) }) }) }), createPortal(_jsx(DragOverlay, { dropAnimation: dropAnimation, children: activeCard ? (_jsx(Card, { card: activeCard, isDragOverlay: true })) : null }), document.body)] }, "dndContext")] }) }) }) }), _jsxs(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragStart: onCategoryDragStart, onDragEnd: onCategoryDragEnd, children: [_jsx(CardSortingCategoryGrid, { "data-testid": "card-sort-categories", children: _jsx(SortableContext, { items: categories, strategy: rectSortingStrategy, children: _jsx(FocusManagedList, { items: categories, onAddItem: onAddNewCategory, renderItem: ({ key, item, ref, onKeyDown }) => (_jsx(Category, { category: item, titleInputRef: ref, onTitleKeyDown: onKeyDown }, key)), renderAddItem: ({ addItem }) => canAddNewCategories ? (_jsx(AddNewCategory, { onClick: addItem, onBulkAddClick: handleBulkAddCategoriesClick })) : null }) }) }), createPortal(_jsx(DragOverlay, { dropAnimation: dropAnimation, children: activeCategory ? (_jsx(Category, { category: activeCategory, isDragOverlay: true })) : null }), document.body)] }, "dndContext"), _jsx(BulkAddItemsModal, { entity: "card", isOpen: isBulkAddCardsModalOpen, onOpenChange: setBulkAddCardsModalOpen }), _jsx(BulkAddItemsModal, { entity: "category", isOpen: isBulkAddCategoriesModalOpen, onOpenChange: setBulkAddCategoriesModalOpen })] }));
});
